import {
  ChangeEvent,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  createColumnHelper,
  ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { ChevronDown, ChevronUp } from '@carbon/react/icons';
import {
  Dropdown,
  Tag,
  InlineNotification,
  Search,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@carbon/react';
import { debounce } from 'lodash-es';

import { Date } from 'components/date';
import { FormSection } from 'components/forms';
import {
  AliasPills,
  aliasPillsFromNames,
  EMPTY_TAGS_TEXT,
} from 'components/alias-pills';
import { Link } from 'components/link';
import { PaginatedTable } from 'components/table/paginated-table';
import { SkeletonTableRows } from 'components/skeleton-table-rows';
import { aliasesForRow } from 'lib/asset-aliases';
import request from 'lib/request';
import { formatNumber, formatRange } from 'utils/number';
import type { CRMTractsResponse, EntityResponse } from 'types/api-responses';

import { NoResults } from './no-results';
import styles from './entity-assets-tab.module.scss';

// ─── Non-tract asset shapes (from GET /entities/:id/assets) ──────────────────

interface WellboreItem {
  kind: 'wellbore';
  id: number;
  uwi: string;
  instrumentType: string | null;
  nri: number | null;
  wi: number | null;
  createdAt: string;
  updatedAt: string;
}

interface LtdSlotItem {
  kind: 'ltd_slot';
  id: number;
  slotName: string | null;
  developmentAreaId: number | null;
  instrumentType: string | null;
  nri: number | null;
  wi: number | null;
  createdAt: string;
  updatedAt: string;
}

type NonTractItem = WellboreItem | LtdSlotItem;

// ─── Unified row type for @tanstack/react-table ───────────────────────────────
// Tracts supply subtracts via getSubRows; non-tract rows have no sub-rows.

type AssetRow = CRMTractsResponse | NonTractItem;

// ─── Filter options ───────────────────────────────────────────────────────────

type KindFilter = '' | 'tract' | 'wellbore' | 'ltd_slot';

const TYPE_OPTIONS: { value: KindFilter; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'tract', label: 'Tract' },
  { value: 'wellbore', label: 'Wellbore' },
  { value: 'ltd_slot', label: 'LTD Slot' },
];

const NULL_DISPLAY = '--';
const PAGE_SIZE = 16;
const SEARCH_DEBOUNCE_MS = 400;

// ─── Column widths ────────────────────────────────────────────────────────────

const COL = {
  expander: { size: 48, minSize: 48, maxSize: 48 },
  name: { size: 180, minSize: 180, maxSize: 180 },
  tags: { size: 200, minSize: 200 },
  assetType: { size: 100, minSize: 100, maxSize: 100 },
  crmAccount: { size: 150, minSize: 150 },
  surveyAreaWellSlot: { size: 180, minSize: 180 },
  quarterCall: { size: 120, minSize: 120 },
  netAcres: { size: 100, minSize: 100 },
  netAcreageUnits: { size: 130, minSize: 130 },
  grossAcres: { size: 100, minSize: 100 },
  depthRange: { size: 140, minSize: 140 },
  instrumentType: { size: 130, minSize: 130 },
  vestingDocument: { size: 150, minSize: 150 },
  nri: { size: 110, minSize: 110 },
  wi: { size: 120, minSize: 120 },
  createdAt: { size: 160, minSize: 160 },
  updatedAt: { size: 160, minSize: 160 },
} as const;

const colStyle = (size: number) => ({
  width: `${size}px`,
  minWidth: `${size}px`,
  maxWidth: `${size}px`,
});

// ─── Helpers ──────────────────────────────────────────────────────────────────

function isTract(row: AssetRow): row is CRMTractsResponse {
  return !('kind' in row);
}

function kindLabel(row: AssetRow): string {
  if (isTract(row)) return 'Tract';
  if (row.kind === 'wellbore') return 'Wellbore';
  return 'LTD Slot';
}

/** Props for {@link EntityAssetsTab}. */
export interface EntityAssetsTabProps {
  entity: EntityResponse;
}

/**
 * Unified Assets tab on the Entity show page. Replaces the tract-only
 * "CRM Tracts" tab with a combined table that shows Tracts (with accordion
 * sub-rows for subtracts), Wellbores, and LTD Slots.
 *
 * Data sources:
 *  - Tracts: POST crmTractsAndSubtracts href (ES-backed, paginated)
 *  - Non-tract assets: GET /entities/:id/assets (AR-backed, all returned)
 *
 * @param {EntityAssetsTabProps} props - Entity context
 * @return {JSX.Element} The Assets tab content
 */
export const EntityAssetsTab: FC<EntityAssetsTabProps> = ({ entity }) => {
  const [searchText, setSearchText] = useState('');
  const [kindFilter, setKindFilter] = useState<KindFilter>('');

  // Tract state
  const [tractItems, setTractItems] = useState<CRMTractsResponse[]>([]);
  const [tractTotal, setTractTotal] = useState(0);
  const [tractPage, setTractPage] = useState(1);
  const [tractPageSize, setTractPageSize] = useState(PAGE_SIZE);
  const [tractLoading, setTractLoading] = useState(false);
  const [tractError, setTractError] = useState<string | null>(null);

  // Non-tract asset state
  const [nonTractItems, setNonTractItems] = useState<NonTractItem[]>([]);
  const [nonTractLoading, setNonTractLoading] = useState(false);
  const [nonTractError, setNonTractError] = useState<string | null>(null);

  // Accordion expand state (tracts only)
  const [expanded, setExpanded] = useState<ExpandedState>({});

  const tractAbortRef = useRef<AbortController | null>(null);
  const nonTractAbortRef = useRef<AbortController | null>(null);

  const showTracts = kindFilter === '' || kindFilter === 'tract';
  const showNonTracts =
    kindFilter === '' || kindFilter === 'wellbore' || kindFilter === 'ltd_slot';

  // ── Tract fetch ─────────────────────────────────────────────────────────────

  const fetchTracts = useCallback(
    async (params: { text: string; page: number; pageSize: number }) => {
      const href = entity.resources.crmTractsAndSubtracts?.href;
      if (!href) return;

      tractAbortRef.current?.abort();
      const controller = new AbortController();
      tractAbortRef.current = controller;
      setTractLoading(true);
      setTractError(null);

      type TractOk = {
        results: CRMTractsResponse[];
        totalCount: number;
        currentPage: number;
        pageSize: number;
      };
      const response = await request.post<TractOk, { error: string }>(
        href,
        {
          query: {
            entityId: entity.id,
            page: params.page,
            pageSize: params.pageSize,
            sortAttribute: 'name',
            sortDirection: 'ASC',
            ...(params.text ? { text: params.text } : {}),
          },
        },
        controller
      );

      if (response.type === 'abort') return;
      if (response.type !== 'success') {
        setTractError('Failed to load tracts');
        setTractItems([]);
        setTractTotal(0);
        setTractLoading(false);
        return;
      }

      setTractItems(response.data.results ?? []);
      setTractTotal(response.data.totalCount ?? 0);
      setTractLoading(false);
    },
    [entity.id, entity.resources.crmTractsAndSubtracts]
  );

  // ── Non-tract fetch ──────────────────────────────────────────────────────────

  const fetchNonTracts = useCallback(
    async (params: { text: string; kind: KindFilter }) => {
      nonTractAbortRef.current?.abort();
      const controller = new AbortController();
      nonTractAbortRef.current = controller;
      setNonTractLoading(true);
      setNonTractError(null);

      const search = new URLSearchParams();
      if (params.kind && params.kind !== 'tract')
        search.set('kind', params.kind);
      if (params.text) search.set('text', params.text);

      const qs = search.toString();
      const response = await request.get<NonTractItem[]>(
        `/entities/${entity.id}/assets${qs ? `?${qs}` : ''}`,
        controller
      );

      if ('error' in (response as object)) {
        if ((response as { error: string }).error === 'AbortError') return;
        setNonTractError('Failed to load assets');
        setNonTractItems([]);
        setNonTractLoading(false);
        return;
      }

      setNonTractItems(response as NonTractItem[]);
      setNonTractLoading(false);
    },
    [entity.id]
  );

  // ── Initial load ─────────────────────────────────────────────────────────────

  useEffect(() => {
    fetchTracts({ text: '', page: 1, pageSize: PAGE_SIZE });
    fetchNonTracts({ text: '', kind: '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Debounced search ─────────────────────────────────────────────────────────

  const debouncedFetch = useMemo(
    () =>
      debounce(
        (text: string, kind: KindFilter, page: number, pageSize: number) => {
          if (kind === '' || kind === 'tract') {
            fetchTracts({ text, page, pageSize });
          }
          if (kind === '' || kind === 'wellbore' || kind === 'ltd_slot') {
            fetchNonTracts({ text, kind });
          }
        },
        SEARCH_DEBOUNCE_MS
      ),
    [fetchTracts, fetchNonTracts]
  );

  const handleSearchChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const next = e.target.value;
      setSearchText(next);
      debouncedFetch(next, kindFilter, tractPage, tractPageSize);
    },
    [debouncedFetch, kindFilter, tractPage, tractPageSize]
  );

  const handleKindChange = useCallback(
    (newKind: KindFilter) => {
      debouncedFetch.cancel();
      setKindFilter(newKind);
      setTractPage(1);
      const doTracts = newKind === '' || newKind === 'tract';
      const doNonTracts =
        newKind === '' || newKind === 'wellbore' || newKind === 'ltd_slot';
      if (doTracts)
        fetchTracts({ text: searchText, page: 1, pageSize: tractPageSize });
      if (doNonTracts) fetchNonTracts({ text: searchText, kind: newKind });
    },
    [debouncedFetch, fetchTracts, fetchNonTracts, searchText, tractPageSize]
  );

  const handlePaginate = useCallback(
    ({ page, pageSize }: { page: number; pageSize: number }) => {
      setTractPage(page);
      setTractPageSize(pageSize);
      fetchTracts({ text: searchText, page, pageSize });
    },
    [fetchTracts, searchText]
  );

  // ── tanstack table (tracts only, for accordion) ───────────────────────────────

  const columnHelper = createColumnHelper<AssetRow>();

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'expander',
        ...COL.expander,
        header: () => null,
        cell: ({ row }) => {
          if (!isTract(row.original)) return null;
          if (!row.getCanExpand()) return null;
          return (
            <button
              type="button"
              className={styles.expander}
              aria-label={row.getIsExpanded() ? 'Collapse row' : 'Expand row'}
              aria-expanded={row.getIsExpanded()}
              onClick={row.getToggleExpandedHandler()}
            >
              {row.getIsExpanded() ? <ChevronUp /> : <ChevronDown />}
            </button>
          );
        },
      }),
      columnHelper.accessor(
        (row) => (isTract(row) ? row.name : String(row.id)),
        {
          id: 'name',
          header: 'Name',
          ...COL.name,
          cell: (info) => {
            const row = info.row.original;
            if (!isTract(row)) return row.id;
            const name = row.name;
            if (!name) return NULL_DISPLAY;
            // Both tract and subtract rows go through the full-page CRM
            // Tract Wizard edit route. The `:tract_id` param name is legacy;
            // the route accepts either a tract or subtract salesforce id.
            const salesforceId = row.salesforceId;
            if (!salesforceId) return name;
            return (
              <Link
                to={`/crm/tracts_and_subtracts/${encodeURIComponent(
                  salesforceId
                )}/edit`}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.nameLink}
              >
                {name}
              </Link>
            );
          },
        }
      ),
      // The tract's tags, as the Tract Catalog shows them: the names on the search document the row
      // came from (`aliases`), `--` with none. Wellbores and LTD slots carry no tags, and neither does
      // a subtract row (the index holds a tract's tags, not a subtract's).
      columnHelper.display({
        id: 'tags',
        header: 'Tags',
        ...COL.tags,
        cell: ({ row }) => {
          const asset = row.original;
          if (!isTract(asset)) return NULL_DISPLAY;
          return (
            <AliasPills
              aliases={aliasPillsFromNames(
                aliasesForRow('tract', asset.salesforceId, asset.aliases ?? [])
              )}
              emptyText={EMPTY_TAGS_TEXT}
            />
          );
        },
      }),
      columnHelper.accessor((row) => kindLabel(row), {
        id: 'assetType',
        header: 'Asset Type',
        ...COL.assetType,
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor(
        (row) => (isTract(row) ? row.account?.name : null),
        {
          id: 'crmAccount',
          header: 'CRM Account',
          ...COL.crmAccount,
          cell: (info) => {
            const row = info.row.original;
            if (!isTract(row)) return NULL_DISPLAY;
            const account = row.account;
            if (!account?.name) return NULL_DISPLAY;
            return account.salesforceHref ? (
              <a
                href={account.salesforceHref}
                target="_blank"
                rel="noopener noreferrer"
              >
                {account.name}
              </a>
            ) : (
              account.name
            );
          },
        }
      ),
      columnHelper.accessor(
        (row) => {
          if (isTract(row)) return row.surveyArea;
          if (row.kind === 'wellbore') return row.uwi;
          return row.slotName;
        },
        {
          id: 'surveyAreaWellSlot',
          header: 'Survey Area / Well / Slot',
          ...COL.surveyAreaWellSlot,
          cell: (info) => {
            const row = info.row.original;
            if (isTract(row)) return row.surveyArea ?? NULL_DISPLAY;
            if (row.kind === 'wellbore') {
              return (
                <Link to={`/wells/${row.uwi}`} target="_blank">
                  {row.uwi}
                </Link>
              );
            }
            // ltd_slot
            if (!row.slotName) return NULL_DISPLAY;
            return row.developmentAreaId ? (
              <Link
                to={`/development_areas/${row.developmentAreaId}`}
                target="_blank"
              >
                {row.slotName}
              </Link>
            ) : (
              row.slotName
            );
          },
        }
      ),
      columnHelper.accessor((row) => (isTract(row) ? row.quarterCall : null), {
        id: 'quarterCall',
        header: 'Quarter Call',
        ...COL.quarterCall,
        cell: (info) => {
          const row = info.row.original;
          return isTract(row)
            ? (row.quarterCall ?? NULL_DISPLAY)
            : NULL_DISPLAY;
        },
      }),
      columnHelper.accessor((row) => (isTract(row) ? row.netAcres : null), {
        id: 'netAcres',
        header: 'Net Acres',
        ...COL.netAcres,
        cell: (info) => {
          const row = info.row.original;
          if (!isTract(row)) return NULL_DISPLAY;
          return formatNumber({
            value: row.netAcres,
            minPrecision: 0,
            maxPrecision: 8,
            includeCommas: true,
          });
        },
      }),
      columnHelper.accessor(
        (row) => (isTract(row) ? row.netAcreageUnits : null),
        {
          id: 'netAcreageUnits',
          header: 'Net Acreage Units',
          ...COL.netAcreageUnits,
          cell: (info) => {
            const row = info.row.original;
            return isTract(row)
              ? (row.netAcreageUnits ?? NULL_DISPLAY)
              : NULL_DISPLAY;
          },
        }
      ),
      columnHelper.accessor((row) => (isTract(row) ? row.grossAcres : null), {
        id: 'grossAcres',
        header: 'Gross Acres',
        ...COL.grossAcres,
        cell: (info) => {
          const row = info.row.original;
          if (!isTract(row)) return NULL_DISPLAY;
          return formatNumber({
            value: row.grossAcres,
            minPrecision: 0,
            maxPrecision: 8,
            includeCommas: true,
          });
        },
      }),
      columnHelper.accessor((row) => (isTract(row) ? row.startDepth : null), {
        id: 'depthRange',
        header: 'Depth Range',
        ...COL.depthRange,
        cell: (info) => {
          const row = info.row.original;
          if (!isTract(row)) return NULL_DISPLAY;
          return formatRange({ start: row.startDepth, end: row.endDepth });
        },
      }),
      columnHelper.accessor(
        (row) => (isTract(row) ? row.instrumentType : row.instrumentType),
        {
          id: 'instrumentType',
          header: 'Instrument Type',
          ...COL.instrumentType,
          cell: (info) => info.row.original.instrumentType ?? NULL_DISPLAY,
        }
      ),
      columnHelper.accessor(
        (row) => (isTract(row) ? row.vestingDocument : null),
        {
          id: 'vestingDocument',
          header: 'Vesting Document',
          ...COL.vestingDocument,
          cell: (info) => {
            const row = info.row.original;
            // Wellbores and LTD slots are not tracts and cannot have a deed at all, so they take
            // the same dash a tract with none takes — this table mixes all three kinds.
            if (!isTract(row) || !row.vestingDocument) return NULL_DISPLAY;

            const vesting = row.vestingDocument;

            return (
              <Link to={vesting.documentHref}>
                <Tag type={vesting.verified ? 'green' : 'magenta'}>
                  {vesting.verified ? 'Verified' : 'Unverified'}
                </Tag>
              </Link>
            );
          },
        }
      ),
      columnHelper.accessor((row) => (isTract(row) ? row.nri : row.nri), {
        id: 'nri',
        header: 'NRI',
        ...COL.nri,
        cell: (info) => {
          const v = info.row.original.nri ?? null;
          if (v === null) return NULL_DISPLAY;
          return formatNumber({
            value: v,
            minPrecision: 0,
            maxPrecision: 8,
            includeCommas: false,
          });
        },
      }),
      columnHelper.accessor(
        (row) => {
          if (isTract(row)) return row.workingInterest ?? null;
          return row.wi ?? null;
        },
        {
          id: 'wi',
          header: 'Working Interest',
          ...COL.wi,
          cell: (info) => {
            const row = info.row.original;
            const v = isTract(row)
              ? (row.workingInterest ?? null)
              : (row.wi ?? null);
            if (v === null) return NULL_DISPLAY;
            return formatNumber({
              value: v,
              minPrecision: 0,
              maxPrecision: 8,
              includeCommas: false,
            });
          },
        }
      ),
      columnHelper.accessor((row) => row.createdAt, {
        id: 'createdAt',
        header: 'Created Date',
        ...COL.createdAt,
        cell: (info) => (
          <span className={styles.truncateText}>
            <Date date={info.getValue()} format="M/DD/YYYY h:mm A" showTime />
          </span>
        ),
      }),
      columnHelper.accessor((row) => row.updatedAt, {
        id: 'updatedAt',
        header: 'Updated Date',
        ...COL.updatedAt,
        cell: (info) => (
          <span className={styles.truncateText}>
            <Date date={info.getValue()} format="M/DD/YYYY h:mm A" showTime />
          </span>
        ),
      }),
    ],
    [columnHelper]
  );

  // ── Combine non-tract + tract rows for the single table ───────────────────────
  // Non-tract items go first (no sub-rows), then tracts.

  const allRows: AssetRow[] = useMemo(
    () => [
      ...(showNonTracts ? nonTractItems : []),
      ...(showTracts ? tractItems : []),
    ],
    [showNonTracts, nonTractItems, showTracts, tractItems]
  );

  const table = useReactTable<AssetRow>({
    data: allRows,
    columns,
    state: { expanded },
    onExpandedChange: setExpanded,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSubRows: (row) => {
      if (!isTract(row)) return [];
      return (row.subtracts as unknown as AssetRow[]) ?? [];
    },
  });

  const totalCols = columns.length;
  const isLoading = tractLoading || nonTractLoading;
  const anyError = tractError ?? nonTractError ?? null;
  const totalCount =
    (showNonTracts ? nonTractItems.length : 0) + (showTracts ? tractTotal : 0);

  const tableHeader = (
    <TableHead>
      {table.getHeaderGroups().map((hg) => (
        <TableRow key={hg.id}>
          {hg.headers.map((header) => (
            <TableHeader
              key={header.id}
              style={colStyle(header.column.getSize())}
            >
              {header.isPlaceholder
                ? null
                : flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
            </TableHeader>
          ))}
        </TableRow>
      ))}
    </TableHead>
  );

  const tableBody = (
    <TableBody>
      {table.getRowModel().rows.map((row) => (
        <TableRow key={row.id}>
          {row.getVisibleCells().map((cell) => (
            <TableCell
              key={cell.id}
              style={{
                paddingLeft: row.depth > 0 ? row.depth * 16 : undefined,
                ...colStyle(cell.column.getSize()),
              }}
            >
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </TableCell>
          ))}
        </TableRow>
      ))}
    </TableBody>
  );

  return (
    <FormSection title="Assets">
      <div className={styles.toolbar}>
        <Dropdown
          id="entity-assets-type-filter"
          label="Type"
          titleText="Type"
          items={TYPE_OPTIONS}
          itemToString={(item) => item?.label ?? ''}
          selectedItem={
            TYPE_OPTIONS.find((o) => o.value === kindFilter) ?? TYPE_OPTIONS[0]
          }
          onChange={({ selectedItem }) => {
            if (selectedItem) handleKindChange(selectedItem.value);
          }}
          size="md"
          className={styles.typeFilter}
        />
        <Search
          id="entity-assets-search"
          labelText="Search"
          placeholder="Search assets"
          size="md"
          value={searchText}
          onChange={handleSearchChange}
          className={styles.search}
        />
      </div>

      {anyError && (
        <InlineNotification
          kind="error"
          title="Failed to load assets"
          subtitle={anyError}
          hideCloseButton
          lowContrast
          className={styles.error}
        />
      )}

      <div className={styles.tableWrapper}>
        <PaginatedTable
          paginationOnTop
          persistentPagination
          pageSize={tractPageSize}
          pageSizes={[16, 24, 48, 100]}
          page={tractPage}
          totalItems={totalCount}
          isLoading={isLoading}
          onPaginate={handlePaginate}
          className={styles.assetsTable}
          loadingComponent={
            <Table className={styles.assetsTable}>
              {tableHeader}
              <TableBody>
                <SkeletonTableRows rows={tractPageSize} columns={totalCols} />
              </TableBody>
            </Table>
          }
          emptyStateComponent={
            <Table className={styles.assetsTable}>
              {tableHeader}
              <TableBody>
                <TableRow>
                  <TableCell colSpan={totalCols}>
                    <NoResults />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          }
        >
          {tableHeader}
          {tableBody}
        </PaginatedTable>
      </div>

      <p className={styles.meta}>
        {totalCount > 0 && `${totalCount} asset${totalCount === 1 ? '' : 's'}`}
      </p>
    </FormSection>
  );
};
